import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, CheckCircle, CreditCard, WarningCircle, ArrowClockwise, X } from "@phosphor-icons/react";
import client from "../lib/api";
import { track } from "../lib/analytics";
import { fmtDate } from "../lib/helpers";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const SOURCE_LABEL = { stripe: "Paid subscription", points: "Redeemed with points", manual: "Complimentary (Reelty granted)", trial: "Free trial" };

export default function MembershipPanel() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [chosenInterval, setChosenInterval] = useState("month");

  const load = () => client.get("/penthouse/me").then((r) => setMe(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const money = (n) => "$" + Number(n || 0).toLocaleString();

  const subscribe = async (interval = chosenInterval) => {
    setBusy(true);
    try {
      const { data } = await client.post("/penthouse/subscribe", { origin_url: window.location.origin, interval });
      track("begin_checkout", { currency: "USD", item_name: `penthouse_${interval}`, value: interval === "year" ? me?.annual : me?.monthly });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start checkout"); setBusy(false); }
  };

  const manageBilling = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/penthouse/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't open billing portal"); setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true); setConfirmOpen(false);
    try {
      const { data } = await client.post("/penthouse/cancel", {});
      toast.success(data.mode === "period_end" ? "Your membership will end at the close of this billing period." : "Membership cancelled.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't cancel"); }
    setBusy(false);
  };

  const resume = async () => {
    setBusy(true);
    try {
      await client.post("/penthouse/resume", {});
      toast.success("Membership resumed — you'll keep your Penthouse perks.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't resume"); }
    setBusy(false);
  };

  const switchPlan = async (interval) => {
    if (me.interval === interval) return;
    setBusy(true);
    try {
      const { data } = await client.post("/penthouse/switch-plan", { interval });
      toast.success(data.changed
        ? (interval === "year" ? "Switched to annual billing — two months free." : "Switched to monthly billing.")
        : "You're already on that plan.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't switch plan"); }
    setBusy(false);
  };

  if (!me) return <p className="text-sm text-[#5C5A58]" data-testid="membership-loading">Loading membership…</p>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="membership-panel">
      <div>
        <p className="eyebrow text-xs text-[#B08D57] mb-1">Penthouse Partner</p>
        <h2 className="font-display text-3xl text-[#4A0000]">Membership</h2>
        <p className="text-sm text-[#5C5A58] mt-1">Manage your Penthouse Partner plan, billing and renewal.</p>
      </div>

      {me.active ? (
        <>
          {/* Status card */}
          <div className="rounded-2xl bg-[#4A0000] text-white p-6 relative overflow-hidden" data-testid="membership-status">
            <div className="absolute -top-14 -right-14 w-48 h-48 rounded-full bg-[#B08D57]/20 blur-2xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 bg-[#B08D57] text-[#1A1715] font-semibold rounded-full px-3 py-1 text-xs">
                <CheckCircle size={15} weight="fill" /> Active Penthouse Partner
              </span>
              <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Plan</p>
                  <p className="font-medium mt-0.5" data-testid="membership-source">{SOURCE_LABEL[me.source] || "Active"}</p>
                </div>
                {me.since && (
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wide">Member since</p>
                    <p className="font-medium mt-0.5">{fmtDate(me.since)}</p>
                  </div>
                )}
                {me.source === "stripe" && me.current_period_end && (
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wide">{me.cancel_at_period_end ? "Access until" : "Renews on"}</p>
                    <p className="font-medium mt-0.5" data-testid="membership-renewal">{fmtDate(me.current_period_end)}</p>
                  </div>
                )}
                {me.source === "points" && me.until && (
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wide">Access until</p>
                    <p className="font-medium mt-0.5">{fmtDate(me.until)}</p>
                  </div>
                )}
                {me.trial && me.until && (
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wide">Free trial ends</p>
                    <p className="font-medium mt-0.5" data-testid="membership-trial-ends">{fmtDate(me.until)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Trial → paid conversion (launch promo on annual) */}
          {me.trial && (
            <div className="rounded-2xl border border-[#B08D57]/40 bg-[#B08D57]/8 p-6" data-testid="membership-convert">
              <span className="inline-flex items-center gap-1.5 text-[#7E1119] text-xs font-bold uppercase tracking-widest">
                <Crown size={15} weight="fill" /> Keep your Penthouse perks
              </span>
              <h3 className="font-display text-2xl text-[#4A0000] mt-2">Add a card to continue</h3>
              <p className="text-sm text-[#5C5A58] mt-1.5 max-w-md">
                You're on a free trial{me.trial_days_left != null ? ` — ${me.trial_days_left} day${me.trial_days_left === 1 ? "" : "s"} left` : ""}.
                Add a card now to keep 360° tours, drone, priority scheduling & partner pricing. You won't be charged until your trial ends.
              </p>

              <div className="grid sm:grid-cols-2 gap-2 mt-4 max-w-md">
                {[
                  { key: "month", title: "Monthly", price: `${money(me.monthly)}/mo`, note: "Billed monthly after trial" },
                  { key: "year", title: "Annual", price: `${money(me.annual)}/yr`,
                    note: `Launch promo: ${money(me.promo_year1)} first year, then ${money(me.annual)}/yr` },
                ].map((p) => {
                  const on = chosenInterval === p.key;
                  return (
                    <button key={p.key} data-testid={`convert-plan-${p.key}`} onClick={() => setChosenInterval(p.key)}
                      className={`relative text-left rounded-lg border p-3 transition ${on ? "border-[#B08D57] bg-white ring-1 ring-[#B08D57]/40" : "border-[#E9DDC6] bg-white/70 hover:border-[#D9CFB0]"}`}>
                      {p.key === "year" && <span className="absolute -top-2 right-2 text-[9px] font-bold uppercase tracking-wide bg-[#7E1119] text-white rounded-full px-1.5 py-0.5">Best deal</span>}
                      <span className="flex items-center gap-1.5 font-medium text-[#1A1715]">{on && <CheckCircle size={14} weight="fill" className="text-[#B08D57]" />}{p.title}</span>
                      <span className="block font-display text-base text-[#4A0000] mt-0.5">{p.price}</span>
                      <span className="block text-[11px] text-[#8A8782] mt-0.5">{p.note}</span>
                    </button>
                  );
                })}
              </div>

              <Button data-testid="membership-convert-btn" onClick={() => subscribe()} disabled={busy} className="mt-4 bg-[#4A0000] hover:bg-[#5C1A1A] text-white font-semibold">
                <CreditCard size={16} weight="fill" className="mr-1.5" />{busy ? "Starting…" : "Add card & continue"}
              </Button>
              <p className="text-[11px] text-[#8A8782] mt-2">No charge until your trial ends · cancel anytime.</p>
            </div>
          )}

          {/* Plan switcher (Stripe-managed subs only) */}
          {me.manageable && me.interval && (
            <div className="rounded-xl border border-[#E9DDC6] bg-white p-4" data-testid="membership-plan-switch">
              <p className="text-sm font-medium text-[#1A1715] mb-2">Billing plan</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  { key: "month", title: "Monthly", price: money(me.monthly) + "/mo", note: "Billed every month" },
                  { key: "year", title: "Annual", price: money(me.annual) + "/yr", note: "Two months free" },
                ].map((p) => {
                  const on = me.interval === p.key;
                  return (
                    <button key={p.key} data-testid={`plan-${p.key}`} onClick={() => switchPlan(p.key)} disabled={busy || on}
                      className={`text-left rounded-lg border p-3 transition disabled:cursor-default ${on ? "border-[#B08D57] bg-[#B08D57]/10" : "border-[#E9DDC6] bg-white hover:border-[#D9CFB0]"}`}>
                      <span className="flex items-center justify-between">
                        <span className="font-medium text-[#1A1715] flex items-center gap-1.5">
                          {on && <CheckCircle size={14} weight="fill" className="text-[#B08D57]" />}{p.title}
                        </span>
                        <span className="font-mono text-sm text-[#4A0000]">{p.price}</span>
                      </span>
                      <span className="block text-[11px] text-[#8A8782] mt-0.5">{on ? "Current plan" : p.note}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#8A8782] mt-2">Switching takes effect right away; Stripe prorates the difference.</p>
            </div>
          )}

          {/* Cancellation scheduled */}
          {me.cancel_at_period_end && (
            <div className="rounded-xl border border-[#B4472E]/30 bg-[#B4472E]/8 p-4 flex items-start gap-3" data-testid="membership-cancel-scheduled">
              <WarningCircle size={20} weight="fill" className="text-[#B4472E] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#8a2f1c]">Your membership is set to cancel at the end of this billing period.</p>
                <p className="text-xs text-[#8a2f1c]/80 mt-0.5">You keep every Penthouse perk until then. Change your mind?</p>
                <Button data-testid="membership-resume" onClick={resume} disabled={busy} size="sm" className="mt-2 bg-[#4A0000] hover:bg-[#5C1A1A] text-white">
                  <ArrowClockwise size={15} className="mr-1.5" />Resume membership
                </Button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {me.manageable && (
              <Button data-testid="membership-manage-billing" onClick={manageBilling} disabled={busy} variant="outline" className="border-[#E9DDC6] text-[#4A0000]">
                <CreditCard size={16} weight="fill" className="mr-1.5" />Manage billing & payment
              </Button>
            )}
            {!me.cancel_at_period_end && !me.trial && (
              <Button data-testid="membership-cancel" onClick={() => setConfirmOpen(true)} disabled={busy} variant="outline" className="border-[#B4472E]/40 text-[#B4472E] hover:bg-[#B4472E]/5">
                <X size={16} weight="bold" className="mr-1.5" />Cancel membership
              </Button>
            )}
          </div>
          {me.source === "manual" && <p className="text-xs text-[#8A8782]">This is a complimentary membership granted by Reelty. Cancelling removes your perks immediately.</p>}
        </>
      ) : (
        /* Not a member */
        <div className="rounded-2xl border border-[#E9DDC6] bg-white p-6" data-testid="membership-inactive">
          <span className="inline-flex items-center gap-1.5 text-[#7E1119] text-xs font-bold uppercase tracking-widest"><Crown size={15} weight="fill" /> Not a partner yet</span>
          <h3 className="font-display text-2xl text-[#4A0000] mt-2">Unlock Penthouse Partner</h3>
          <p className="text-sm text-[#5C5A58] mt-1.5 max-w-md">360° tours & drone on every All-Inclusive, priority scheduling, partner pricing, LeadLander & ListAssist. Try it free for 30 days — pick your plan below.</p>

          {/* Plan chooser — 30-day free trial either way, default monthly */}
          <div className="grid sm:grid-cols-2 gap-2 mt-4 max-w-md">
            {[
              { key: "month", title: "Monthly", price: `${money(me.monthly)}/mo`, note: "Billed monthly after trial" },
              { key: "year", title: "Annual", price: `${money(me.annual)}/yr`, note: `2 months free · save ${money(Math.max(0, me.monthly * 12 - me.annual))}` },
            ].map((p) => {
              const on = chosenInterval === p.key;
              return (
                <button key={p.key} data-testid={`membership-plan-${p.key}`} onClick={() => setChosenInterval(p.key)}
                  className={`relative text-left rounded-lg border p-3 transition ${on ? "border-[#B08D57] bg-[#B08D57]/10 ring-1 ring-[#B08D57]/40" : "border-[#E9DDC6] bg-white hover:border-[#D9CFB0]"}`}>
                  {p.key === "year" && <span className="absolute -top-2 right-2 text-[9px] font-bold uppercase tracking-wide bg-[#B08D57] text-[#1A1715] rounded-full px-1.5 py-0.5">Best value</span>}
                  <span className="flex items-center gap-1.5 font-medium text-[#1A1715]">{on && <CheckCircle size={14} weight="fill" className="text-[#B08D57]" />}{p.title}</span>
                  <span className="block font-display text-base text-[#4A0000] mt-0.5">{p.price}</span>
                  <span className="block text-[11px] text-[#8A8782] mt-0.5">{p.note}</span>
                </button>
              );
            })}
          </div>

          <Button data-testid="membership-subscribe" onClick={() => subscribe()} disabled={busy} className="mt-4 bg-[#B08D57] hover:bg-[#C59B47] text-[#1A1715] font-semibold">
            <Crown size={16} weight="fill" className="mr-1.5" />{busy ? "Starting…" : "Start free"}
          </Button>
          <p className="text-[11px] text-[#8A8782] mt-2">No charge for 30 days · cancel anytime.</p>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="membership-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Penthouse Partner?</AlertDialogTitle>
            <AlertDialogDescription>
              {me.source === "stripe"
                ? "Your membership stays active until the end of the current billing period, then won't renew. You can resume anytime before then."
                : "This ends your Penthouse perks right away."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="membership-cancel-dismiss">Keep membership</AlertDialogCancel>
            <AlertDialogAction data-testid="membership-cancel-confirm" onClick={cancel} className="bg-[#B4472E] hover:bg-[#9a3a25]">Yes, cancel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
